import { RawTexture } from '@babylonjs/core/Materials/Textures/rawTexture';
import type { BaseTexture } from '@babylonjs/core/Materials/Textures/baseTexture';
import type { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Logger } from './Logger';

/** The metallic-roughness channels a factor override takes over: B is metalness, G is roughness (glTF). */
export interface FactorChannels {
    metallic: boolean;
    roughness: boolean;
}

/** RawTexture.clone shares pixels but resets texture coordinates and sampler settings. */
export function copyFactorTextureSettings(source: Texture, target: Texture): void {
    Object.assign(target, {
        gammaSpace: source.gammaSpace,
        level: source.level,
        wrapU: source.wrapU,
        wrapV: source.wrapV,
        wrapR: source.wrapR,
        anisotropicFilteringLevel: source.anisotropicFilteringLevel,
        coordinatesIndex: source.coordinatesIndex,
        coordinatesMode: source.coordinatesMode,
        optimizeUVAllocation: source.optimizeUVAllocation,
        uOffset: source.uOffset,
        vOffset: source.vOffset,
        uScale: source.uScale,
        vScale: source.vScale,
        uAng: source.uAng,
        vAng: source.vAng,
        wAng: source.wAng,
        uRotationCenter: source.uRotationCenter,
        vRotationCenter: source.vRotationCenter,
        wRotationCenter: source.wRotationCenter,
        homogeneousRotationInUVTransform: source.homogeneousRotationInUVTransform,
    });
    target.updateSamplingMode(source.samplingMode);
}

/**
 * Raise the overridden channels of RGBA `pixels` to full scale in place, so a
 * factor the shader multiplies by the texture renders as the factor itself.
 * False when those channels were already at full scale everywhere.
 */
export function forceFactorChannels(pixels: Uint8Array, channels: FactorChannels): boolean {
    let changed = false;
    for (let i = 0; i < pixels.length; i += 4) {
        if (channels.roughness && pixels[i + 1] !== 255) {
            pixels[i + 1] = 255;
            changed = true;
        }
        if (channels.metallic && pixels[i + 2] !== 255) {
            pixels[i + 2] = 255;
            changed = true;
        }
    }
    return changed;
}

/** The parts of a metallic-roughness texture {@link materialOwnFinish} reads. */
type ReadableTexture = Pick<BaseTexture, 'name' | 'isReady' | 'readPixels'>;

const TEXTURE_WAIT_MS = 5000;
const TEXTURE_POLL_MS = 50;

async function loaded(texture: ReadableTexture): Promise<boolean> {
    const deadline = Date.now() + TEXTURE_WAIT_MS;
    while (!texture.isReady()) {
        if (Date.now() >= deadline) {
            Logger.warn(`[FactorTexture] "${texture.name}" did not load within ${TEXTURE_WAIT_MS / 1000} s; nothing was read from it`);
            return false;
        }
        await new Promise(resolve => setTimeout(resolve, TEXTURE_POLL_MS));
    }
    return true;
}

/** The means of a loaded texture's G (roughness) and B (metallic) channels, from 0 to 1; null when its pixels can't be read back. */
async function channelMeans(texture: ReadableTexture): Promise<Record<keyof FactorChannels, number> | null> {
    let pixels: ArrayBufferView | null;
    try {
        pixels = await texture.readPixels();
    } catch {
        return null;
    }
    if (!(pixels instanceof Uint8Array) || pixels.length === 0) return null;
    let roughness = 0;
    let metallic = 0;
    for (let i = 0; i < pixels.length; i += 4) {
        roughness += pixels[i + 1];
        metallic += pixels[i + 2];
    }
    const scale = (pixels.length / 4) * 255;
    return { roughness: roughness / scale, metallic: metallic / scale };
}

const unit = (value: number): number => Math.min(1, Math.max(0, value));
const factorOf = (value: number | null | undefined): number => (typeof value === 'number' && Number.isFinite(value) ? value : 1);

/**
 * The roughness and metallic a PBR material shows on a slot: each factor times
 * the mean of its metallic-roughness texture's channel. A texture that can't
 * be read back keeps multiplying, so the factor alone stands. Empty when the
 * texture doesn't load within five seconds: the caller picks the fallback.
 */
export async function materialOwnFinish(material: {
    roughness?: number | null;
    metallic?: number | null;
    metallicTexture?: ReadableTexture | null;
}): Promise<{ roughness?: number; metallic?: number }> {
    const texture = material.metallicTexture;
    if (texture && !(await loaded(texture))) return {};
    const means = texture ? await channelMeans(texture) : null;
    return {
        roughness: unit(factorOf(material.roughness) * (means?.roughness ?? 1)),
        metallic: unit(factorOf(material.metallic) * (means?.metallic ?? 1)),
    };
}

/**
 * A copy of `source`'s pixels with the overridden channels forced (see
 * forceFactorChannels), in the same orientation. Null when the source needs
 * no change, when it isn't loaded yet (it would read back as zeros, so a
 * caller waits for the load first), or when its pixels can't be read back (a
 * compressed texture); in the last two the override keeps multiplying.
 */
export async function deriveFactorTexture(source: Texture, channels: FactorChannels): Promise<Texture | null> {
    if (!source.isReady()) {
        Logger.warn(`[FactorTexture] "${source.name}" is not loaded yet; its factor override keeps multiplying the texture`);
        return null;
    }
    // readPixels hands back a buffer of its own, so it is rewritten in place.
    const pixels = await source.readPixels();
    if (!(pixels instanceof Uint8Array)) {
        Logger.warn(`[FactorTexture] Can't read "${source.name}" back; its factor override keeps multiplying the texture`);
        return null;
    }
    if (!forceFactorChannels(pixels, channels)) return null;
    const { width, height } = source.getSize();
    const derived = RawTexture.CreateRGBATexture(pixels, width, height, source.getScene(), !source.noMipmap, false, source.samplingMode);
    derived.name = `${source.name} (factor override)`;
    return derived;
}
